import type { FC } from "react";
import { createPortal } from "react-dom";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";

import type { Language, PopupContext } from "../../LanguageSelector";

import { LanguageSelectorList } from "./components/LanguageSelectorList";

import styles from "./LanguageSelectorPopupMobileFullscreen.module.scss";

type LanguageSelectorPopupMobileFullscreenProps = {
  languages: Language[];
  onApplyLanguage: (language: Language) => void;
  onClose: () => void;
  isOpened: boolean;
  popupContext: PopupContext;
};

export const LanguageSelectorPopupMobileFullscreen: FC<LanguageSelectorPopupMobileFullscreenProps> = ({
  languages,
  onClose,
  onApplyLanguage,
  isOpened,
  popupContext,
}) => {
  const { __ } = useTranslations();

  const currentPageLanguage = languages.find(({ current }) => current);

  const handleSelectLanguage = (selectedLanguage: Language) => {
    onApplyLanguage(selectedLanguage);
    onClose();
  };

  if (!isOpened) {
    return null;
  }

  return createPortal(
    <div
      data-testid="languageSelectorPopupMobileFullscreen"
      className={classNames("languageSelectorPopupMobileFullscreen", styles.modal, {
        "light-theme": popupContext === "AGE_CONFIRMATION",
      })}
    >
      <div className={styles.container}>
        <div className={styles.content}>
          <h3 className={styles.title}>{__("languageselector.select_language.headline")}</h3>
          <LanguageSelectorList
            languages={languages}
            selectedLanguage={currentPageLanguage}
            onSelectLanguage={handleSelectLanguage}
          />

          <Button
            onClick={onClose}
            variant="secondary"
            size="sm"
            className={styles.button}
            data-testid="languageSelectorCancelBtn"
          >
            {__("languageselector.cancel.btn")}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
